import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import AppIcon from '../../../../components/AppIcon';
import { Card } from '../../../../components/ui/Card';
import type { Goal, Filters, DepartmentBasicView } from '../types';

interface FilterPanelProps {
  isOpen?: boolean;
  onToggle?: () => void;
  filters?: Filters;
  onFilterChange?: (filters: Filters) => void;
  isMobile?: boolean;
  allGoals?: Goal[];
  departments?: DepartmentBasicView[];
  loadingDepartments?: boolean;
}

const FilterPanel: React.FC<FilterPanelProps> = ({
  isOpen = true,
  onToggle = () => {},
  filters = {
    search: '',
    department: 'all',
    user: 'all',
    dueDate: 'all',
    status: [],
    levels: [],
  },
  onFilterChange = () => {},
  isMobile = false,
  allGoals = [],
  departments = [],
  loadingDepartments = false,
}) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState<string>(filters.search || '');

  const departmentOptions = [
    { value: 'all', label: t('All Departments') },
    ...departments.map(dept => ({
      value: dept.id ? dept.id.toString() : dept.name.toLowerCase(),
      label: dept.name
    }))
  ];

  const userOptions = [
    { value: 'all', label: t('All Users') },
    ...Array.from(new Set(allGoals.filter(g => g.owner).map(g => g.owner)))
      .map(owner => ({ value: owner, label: owner })),
  ];

  const dueDateOptions = [
    { value: 'all', label: t('All Due Dates') },
    { value: 'overdue', label: t('Overdue') },
    { value: 'this-week', label: t('This Week') },
    { value: 'this-month', label: t('This Month') },
    { value: 'next-quarter', label: t('Next Quarter') },
    { value: 'current-year', label: t('Current Year') },
  ];

  const statusOptions = [
    { value: 'on-track', label: t('On Track') },
    { value: 'at-risk', label: t('At Risk') },
    { value: 'completed', label: t('Completed') },
    { value: 'overdue', label: t('Overdue') },
  ];

  const levelOptions = [
    { value: 'company', label: t('Company Goals') },
    { value: 'team', label: t('Team Goals') },
    { value: 'individual', label: t('Individual Goals') },
  ];

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    onFilterChange({ ...filters, search: value });
  };

  const handleDepartmentChange = (value: string) => {
    onFilterChange({ ...filters, department: value });
  };

  const handleUserChange = (value: string) => {
    onFilterChange({ ...filters, user: value });
  };

  const handleDueDateChange = (value: string) => {
    onFilterChange({ ...filters, dueDate: value });
  };

  const handleStatusChange = (statusValue: string, checked: boolean) => {
    const currentStatus = filters.status || [];
    const newStatus = checked
      ? [...currentStatus, statusValue]
      : currentStatus.filter(s => s !== statusValue);
    onFilterChange({ ...filters, status: newStatus });
  };

  const handleLevelChange = (levelValue: string, checked: boolean) => {
    const currentLevels = filters.levels || [];
    const newLevels = checked
      ? [...currentLevels, levelValue]
      : currentLevels.filter(l => l !== levelValue);
    onFilterChange({ ...filters, levels: newLevels });
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    onFilterChange({
      search: '',
      department: 'all',
      user: 'all',
      dueDate: 'all',
      status: [],
      levels: [],
      hasBlockers: undefined,
      unclearOnly: undefined,
    });
  };

  const activeFilterCount = Object.values(filters).filter(value => {
    if (Array.isArray(value)) return value.length > 0;
    return value && value !== 'all' && value !== '';
  }).length;

  const panelContent = (
    <div className="p-3">
      {/* Header */}
      <div className="flex justify-between items-center mb-3">
        <div className="flex items-center gap-1">
          <AppIcon name="ListFilter" size={18} className="text-primary" />
          <h2 className="text-base font-medium">
            {t('Filters')}
          </h2>
          {activeFilterCount > 0 && (
            <span className="ms-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs font-medium">
              {activeFilterCount}
            </span>
          )}
        </div>
        {isMobile && (
          <button onClick={onToggle} className="p-1 min-h-[44px] min-w-[44px] hover:bg-muted rounded flex items-center justify-center">
            <AppIcon name="X" size={18} className="text-muted-foreground" />
          </button>
        )}
      </div>

      {/* Search */}
      <div className="mb-3">
        <div className="relative">
          <AppIcon name="Search" size={16} className="absolute start-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder={t('Search goals...')}
            value={searchTerm}
            onChange={handleSearchChange}
            className="w-full ps-8 pe-3 py-2 min-h-[44px] text-sm border border-border rounded-lg bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
          />
        </div>
      </div>

      {/* Goal Level Filter */}
      <div className="mb-3">
        <h3 className="text-sm font-medium mb-1">
          {t('Goal Level')}
        </h3>
        <div className="flex flex-col gap-1">
          {levelOptions.map((option) => (
            <label key={option.value} className="flex items-center gap-2 cursor-pointer text-sm">
              <input
                type="checkbox"
                className="w-4 h-4 rounded border-border text-primary focus-visible:ring-ring"
                checked={filters.levels?.includes(option.value) || false}
                onChange={(e) => handleLevelChange(option.value, e.target.checked)}
              />
              {option.label}
            </label>
          ))}
        </div>
      </div>

      {/* Department Filter */}
      <div className="mb-3">
        <label className="block text-sm font-medium mb-1" htmlFor="goal-filter-department">
          {t('Department')} {loadingDepartments && t('(Loading...)')}
        </label>
        <select
          id="goal-filter-department"
          value={filters.department || 'all'}
          onChange={(e) => handleDepartmentChange(e.target.value)}
          disabled={loadingDepartments || departmentOptions.length === 1}
          className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary disabled:opacity-50 disabled:bg-muted"
        >
          {departmentOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
          {!loadingDepartments && departments.length === 0 && (
            <option disabled value="">
              {t('No departments available')}
            </option>
          )}
        </select>
        {!loadingDepartments && departments.length === 0 && (
          <p className="text-xs text-muted-foreground mt-1">
            {t('No departments found in the database')}
          </p>
        )}
      </div>

      {/* User Filter */}
      {userOptions.length > 1 && (
        <div className="mb-3">
          <label className="block text-sm font-medium mb-1" htmlFor="goal-filter-user">{t('User / Owner')}</label>
          <select
            id="goal-filter-user"
            value={filters.user || 'all'}
            onChange={(e) => handleUserChange(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
          >
            {userOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Due Date Filter */}
      <div className="mb-3">
        <label className="block text-sm font-medium mb-1" htmlFor="goal-filter-due-date">{t('Due Date Filtering')}</label>
        <select
          id="goal-filter-due-date"
          value={filters.dueDate || 'all'}
          onChange={(e) => handleDueDateChange(e.target.value)}
          className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
        >
          {dueDateOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Status Filter */}
      <div className="mb-3">
        <h3 className="text-sm font-medium mb-1">
          {t('Goal Status')}
        </h3>
        <div className="flex flex-col gap-1">
          {statusOptions.map((option) => (
            <label key={option.value} className="flex items-center gap-2 cursor-pointer text-sm">
              <input
                type="checkbox"
                className="w-4 h-4 rounded border-border text-primary focus-visible:ring-ring"
                checked={filters.status?.includes(option.value) || false}
                onChange={(e) => handleStatusChange(option.value, e.target.checked)}
              />
              {option.label}
            </label>
          ))}
        </div>
      </div>

      {/* Blocker Filter */}
      <div className="mb-3">
        <label className="flex items-center gap-2 cursor-pointer text-sm">
          <input
            type="checkbox"
            className="w-4 h-4 rounded border-border text-primary focus-visible:ring-ring"
            checked={filters.hasBlockers || false}
            onChange={(e) => onFilterChange({ ...filters, hasBlockers: e.target.checked || undefined })}
          />
          {t('Has Active Blockers')}
        </label>
      </div>

      {/* Unclear Goals Filter */}
      <div className="mb-3">
        <label className="flex items-center gap-2 cursor-pointer text-sm">
          <input
            type="checkbox"
            className="w-4 h-4 rounded border-border text-primary focus-visible:ring-ring"
            checked={filters.unclearOnly || false}
            onChange={(e) => onFilterChange({ ...filters, unclearOnly: e.target.checked || undefined })}
          />
          {t('goals.filter.unclearOnly')}
        </label>
      </div>

      {/* Color Code Legend */}
      <div className="mb-3">
        <h3 className="text-sm font-medium mb-1">
          {t('Color Code Legend')}
        </h3>
        <ul className="space-y-1">
          <li className="flex items-center gap-2">
            <span className="w-4 h-4 rounded-sm flex-shrink-0" style={{ background: 'linear-gradient(135deg, #2563eb 0%, #8b5cf6 100%)' }} />
            <span className="text-sm text-foreground">{t('Company Goals')}</span>
          </li>
          <li className="flex items-center gap-2">
            <span className="w-4 h-4 rounded-sm flex-shrink-0" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #eab308 100%)' }} />
            <span className="text-sm text-foreground">{t('Team Goals')}</span>
          </li>
          <li className="flex items-center gap-2">
            <span className="w-4 h-4 rounded-sm flex-shrink-0 border-2 border-muted-foreground" />
            <span className="text-sm text-foreground">{t('Individual Goals')}</span>
          </li>
        </ul>
      </div>

      {/* Clear Filters */}
      {activeFilterCount > 0 && (
        <button
          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm border border-border rounded-lg hover:bg-muted transition-colors"
          onClick={clearAllFilters}
        >
          <AppIcon name="RotateCcw" size={14} />
          {t('Clear All Filters')}
        </button>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <>
        {/* Mobile Filter Toggle Button */}
        <button
          className="fixed bottom-5 end-4 z-[1200] w-14 h-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-lg hover:bg-primary/90 transition-colors"
          onClick={onToggle}
        >
          <AppIcon name="ListFilter" size={20} />
          {activeFilterCount > 0 && (
            <span className="absolute -top-2 -end-2 w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-medium flex items-center justify-center">
              {activeFilterCount}
            </span>
          )}
        </button>

        {/* Mobile Filter Drawer */}
        {isOpen && (
          <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/50" onClick={onToggle} />
            <div className="absolute bottom-0 left-0 right-0 max-h-[80vh] bg-card rounded-t-xl overflow-auto">
              {panelContent}
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <Card
      variant="elevated"
      className={`transition-all duration-300 overflow-hidden ${
        isOpen ? 'w-[280px]' : 'w-12'
      }`}
    >
      {isOpen ? (
        panelContent
      ) : (
        <div className="flex justify-center p-2">
          <button onClick={onToggle} className="p-1 hover:bg-muted rounded">
            <AppIcon name="ListFilter" size={18} className="text-muted-foreground" />
          </button>
        </div>
      )}
    </Card>
  );
};

export default FilterPanel;
